import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { ROLE_LABELS } from "@/lib/dates";

const EMPTY = { name: "", email: "", password: "", role: "employee", hourly_rate: "", contract_hours: "", phone: "", active: true };

const Field = ({ id, label, ...rest }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} className="mt-1.5" data-testid={`employee-${id}-input`} {...rest} />
  </div>
);

export const EmployeeDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const api = useApi();
  const { member, isOwner } = useMember();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const editing = !!initial;
  const isSelf = initial?.user_id === member.user_id;
  const roles = isOwner ? ["owner", "manager", "employee"] : ["employee"];

  useEffect(() => {
    if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    const nums = { hourly_rate: Number(String(f.hourly_rate).replace(",", ".")) || 0, contract_hours: Number(f.contract_hours) || 0 };
    try {
      if (editing) {
        const body = { name: f.name, phone: f.phone, ...nums };
        if (!isSelf) Object.assign(body, { role: f.role, active: f.active });
        await api.patch(`/employees/${initial.user_id}`, body);
      } else {
        await api.post("/employees", { name: f.name, email: f.email, password: f.password, role: f.role, phone: f.phone, ...nums });
      }
      toast.success(editing ? "Medewerker bijgewerkt" : "Account aangemaakt. Deel e-mail en wachtwoord met de medewerker.");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Medewerker wijzigen" : "Medewerker toevoegen"}</DialogTitle>
          {!editing && <DialogDescription>Je maakt een account aan met een tijdelijk wachtwoord. De medewerker kan dit later zelf wijzigen.</DialogDescription>}
        </DialogHeader>
        <div className="grid gap-4">
          <Field id="name" label="Naam" value={f.name} onChange={set("name")} placeholder="Bijv. Lars Bakker" />
          {!editing && (
            <>
              <Field id="email" label="E-mailadres" type="email" value={f.email} onChange={set("email")} placeholder="lars@voorbeeld.nl" />
              <Field id="password" label="Tijdelijk wachtwoord" type="text" value={f.password} onChange={set("password")} placeholder="Minimaal 8 tekens" />
            </>
          )}
          {!isSelf && (
            <div>
              <Label>Rol</Label>
              <Select value={f.role} onValueChange={(v) => setF((p) => ({ ...p, role: v }))} disabled={!editing ? false : initial.role === "owner" && !isOwner}>
                <SelectTrigger className="mt-1.5" data-testid="employee-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.filter((r) => editing || r !== "owner").map((r) => <SelectItem key={r} value={r} data-testid={`employee-role-option-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field id="rate" label="Uurloon (€)" inputMode="decimal" value={f.hourly_rate} onChange={set("hourly_rate")} placeholder="14,40" />
            <Field id="contract" label="Contracturen/week" type="number" min="0" value={f.contract_hours} onChange={set("contract_hours")} placeholder="0 = oproep" />
          </div>
          <Field id="phone" label="Telefoon" value={f.phone} onChange={set("phone")} placeholder="06 12345678" />
          {editing && !isSelf && (
            <label className="flex items-center justify-between rounded-xl border border-slate-200 p-3 text-sm">
              <span><b>Actief</b><span className="block text-xs text-slate-500">Inactieve medewerkers kunnen niet meer inloggen in het bedrijf.</span></span>
              <Switch checked={f.active} onCheckedChange={(v) => setF((p) => ({ ...p, active: v }))} data-testid="employee-active-switch" />
            </label>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="w-full sm:w-auto" data-testid="employee-save-button">{editing ? "Opslaan" : "Account aanmaken"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
